export type Language = "en" | "zh";

export const BOOKING_URL = "https://lumacare.square.site/s/appointments";

export const businessConfig = {
  name: "Luma Care",
  phoneDisplay: "949-682-5516",
  phoneDigits: "9496825516",
  price: 129,
  newClientDiscount: 29,
  durationMinutes: 45,
  bookingUrl: BOOKING_URL,
  serviceArea: {
    communities: [] as string[],
    testimonialLocations: ["Fullerton", "Irvine", "Laguna Beach"],
  },
  businessHours: null as string | null, // TODO: Add verified business hours.
  socialLinks: {} as Record<string, string>, // TODO: Add verified social profiles.
  april: {
    photo: null as string | null, // TODO: Replace with April's approved profile photo.
    credentials: [] as string[], // TODO: Add verified credentials only.
  },
  images: {
    hero: "/images/luma-care-hero.webp",
    movement: "/images/everyday-movement.webp",
  },
} as const;

const en = {
  locale: "en-US",
  languageName: "English",
  nav: {
    services: "Services",
    conditions: "What We Help",
    about: "About April",
    reviews: "Reviews",
    faq: "FAQ",
    book: "Book a Session",
  },
  common: {
    book: "Book a Session",
    text: "Text 949-682-5516",
    openMenu: "Open navigation menu",
    closeMenu: "Close navigation menu",
  },
  hero: {
    eyebrow: "In-home rehabilitation • Orange County",
    headline: "Wellness in Motion.\nCare at Home.",
    copy: "Personalized in-home rehabilitation therapy that helps relieve pain, restore mobility, and improve how you move.",
    imageAlt: "A rehabilitation practitioner guiding a client through shoulder mobility work at home",
    trust: "Personal care · Practical recovery · English & 中文",
  },
  benefits: {
    label: "Care that fits real life",
    items: [
      ["In-home care", "No driving or waiting rooms"],
      ["Personalized therapy", "Care based on your pain, mobility, and condition"],
      ["Hands-on treatment", "Focused work for tight, restricted, or sensitive areas"],
      ["Bilingual service", "English and Chinese communication"],
    ],
  },
  problem: {
    eyebrow: "Everyday movement matters",
    title: "Pain changes how you move through your day.",
    copy: "Headaches, back pain, shoulder restriction, postpartum discomfort, and muscle tightness can affect the ordinary movements that make up your day. Luma Care brings focused, personalized care into your home—so support meets you where life happens.",
    imageAlt: "A woman noticing shoulder restriction while reaching into a kitchen cabinet",
  },
  conditions: {
    eyebrow: "What we help with",
    title: "Support for the ways discomfort shows up.",
    copy: "Sessions are shaped around how you feel and move. Luma Care may support clients experiencing:",
    disclaimer: "Luma Care does not diagnose medical conditions. Individual experiences and outcomes vary.",
    items: [
      "Headaches and migraines",
      "Neck and shoulder pain",
      "Lower and upper back pain",
      "Joint discomfort",
      "Muscle tightness",
      "Posture-related pain",
      "Postpartum recovery",
      "Sports and exercise recovery",
      "Reduced mobility",
      "Age-related stiffness",
    ],
  },
  service: {
    eyebrow: "Main service",
    title: "Rehabilitation Therapy",
    chineseTitle: "筋膜物理康复",
    priceLabel: "$129",
    duration: "45 minutes",
    location: "In-home service",
    copy: "Personalized hands-on care intended to help relieve discomfort, restore mobility, and improve how you move.",
    book: "Book Rehabilitation Therapy",
    noteLabel: "To confirm your appointment",
    note: "After booking, text your full service address—including unit number and gate code when applicable—to 949-682-5516. Your appointment is confirmed after the address is received.",
  },
  offer: {
    eyebrow: "New-client welcome",
    title: "Your first session includes $29 off.",
    copy: "New clients and clients referred by family or friends are eligible for one discounted appointment.",
    book: "Book New Client Therapy",
    finePrint: "One discounted appointment per eligible client.",
  },
  process: {
    eyebrow: "How it works",
    title: "From booking to care at home.",
    steps: [
      ["Book", "Choose a service and appointment time."],
      ["Text Your Address", "Send the complete service address and access details."],
      ["Receive Confirmation", "Luma Care confirms the appointment after receiving your address."],
      ["Get Care at Home", "April provides a session based on your condition and mobility needs."],
    ],
  },
  about: {
    eyebrow: "About April",
    title: "Experienced care, delivered with honesty.",
    copy: [
      "April provides personalized hands-on rehabilitation therapy focused on movement, muscle tension, mobility, and practical recovery.",
      "She adjusts each session to the individual and believes good care includes honest guidance—not unnecessary or overly frequent treatment.",
    ],
    imageAlt: "Profile photo placeholder for April",
    placeholder: "April’s photo coming soon",
    credentialsLabel: "Verified credentials will be added here.",
  },
  testimonials: {
    eyebrow: "Client experiences",
    title: "Care that people feel in daily life.",
    disclaimer: "Edited client excerpts. Individual experiences vary.",
    items: [
      {
        name: "Anthony, 33",
        location: "Fullerton, CA",
        quote: "After just one session with April, I noticed a remarkable difference. Nearly two years later, my migraines have not returned the way they used to.",
      },
      {
        name: "M Chen, 28",
        location: "Irvine, CA",
        quote: "After trying acupuncture, massage, and other treatments without meaningful improvement, my lower-back pain improved noticeably after working with April.",
      },
      {
        name: "Scott C., 33",
        location: "Laguna Beach, CA",
        quote: "My shoulder was so restricted that I could not raise my arm to 90 degrees. After one session, I could move it much more freely.",
      },
      {
        name: "Camilla C., 30",
        location: "Irvine, CA",
        quote: "After three sessions, my back pain improved significantly. April also helped me adjust my workouts and refused to recommend treatment more often than my body needed.",
      },
    ],
  },
  expectations: {
    eyebrow: "What to expect",
    title: "This is focused rehabilitation work, not a relaxation massage.",
    copy: "Hands-on work may feel intense when addressing tight or sensitive areas. April adjusts the session based on your feedback, comfort, and physical response, and encourages you to communicate throughout.",
  },
  area: {
    eyebrow: "Service area",
    title: "In-home care in selected Orange County communities.",
    copy: "Service availability depends on location. Text 949-682-5516 to confirm that your address is within the current service area.",
    testimonialNote: "Client testimonials on this page represent Fullerton, Irvine, and Laguna Beach; these locations do not define the full service area.",
  },
  faq: {
    eyebrow: "Questions, answered",
    title: "Before your session.",
    items: [
      ["Is this massage therapy?", "No. This is focused, hands-on rehabilitation work intended to support movement, mobility, and practical recovery—not a relaxation massage."],
      ["Does treatment hurt?", "Work on tight or sensitive areas may feel intense. April adjusts pressure and approach based on your feedback, comfort, and physical response."],
      ["How long is a session?", "A standard rehabilitation therapy session is 45 minutes."],
      ["Where does the appointment take place?", "Luma Care comes to your service address, subject to current location availability."],
      ["How do I confirm my appointment?", "After booking, text your full service address, unit number, and gate code when applicable to 949-682-5516. Your appointment is confirmed after the address is received."],
      ["What should I wear?", "Wear comfortable clothing that allows you to move easily and makes the area of concern reasonably accessible."],
      ["What areas do you serve?", "Luma Care serves selected Orange County communities. Text your address to 949-682-5516 to confirm current availability."],
      ["Is this appropriate for postpartum recovery?", "Postpartum recovery is one area Luma Care may support. Share relevant concerns before the session and consult your medical provider when appropriate."],
      ["Do you accept insurance?", "Insurance participation and payment policy are being confirmed. Please text 949-682-5516 for current information."], // REQUIRES BUSINESS CONFIRMATION
      ["What is the cancellation policy?", "The cancellation and rescheduling policy is being confirmed. Please contact Luma Care before booking if you need the current terms."], // REQUIRES BUSINESS CONFIRMATION
    ],
  },
  finalCta: {
    eyebrow: "Care, on your terms",
    title: "Move better without leaving home.",
    copy: "Choose a time that works for you, then text your address to complete confirmation.",
  },
  footer: {
    tagline: "Personalized in-home rehabilitation therapy in selected Orange County communities.",
    bookingNote: "Appointments are booked securely through Square.",
    copyright: "Luma Care. All rights reserved.",
  },
};

const zh = {
  locale: "zh-CN",
  languageName: "简体中文",
  nav: {
    services: "服务项目",
    conditions: "适用情况",
    about: "关于 April",
    reviews: "客户反馈",
    faq: "常见问题",
    book: "预约理疗",
  },
  common: {
    book: "预约理疗",
    text: "短信联系 949-682-5516",
    openMenu: "打开导航菜单",
    closeMenu: "关闭导航菜单",
  },
  hero: {
    eyebrow: "上门康复理疗 · 橙县",
    headline: "健康随行，护理到家。",
    copy: "专业上门筋膜肌肉康复理疗，帮助缓解疼痛、改善活动能力，让身体恢复得更轻松、更有信心。",
    imageAlt: "理疗师在客户家中协助进行肩部活动训练",
    trust: "贴心服务 · 实用康复 · English & 中文",
  },
  benefits: {
    label: "贴合日常生活的康复服务",
    items: [
      ["上门服务", "无需开车，无需在候诊室等待"],
      ["个性化理疗", "根据疼痛、活动能力和身体状况调整方案"],
      ["专业手法", "针对紧绷、受限或敏感部位进行重点调理"],
      ["双语服务", "提供英语和中文沟通"],
    ],
  },
  problem: {
    eyebrow: "日常活动同样重要",
    title: "疼痛会悄悄改变你的每一天。",
    copy: "头痛、背痛、肩部活动受限、产后不适和肌肉紧绷，都可能影响日常生活中的每一个动作。Luma Care 把专注、个性化的康复服务带到家中，让专业支持融入真实生活。",
    imageAlt: "一位女士伸手取厨房高处物品时感到肩部活动受限",
  },
  conditions: {
    eyebrow: "我们可以帮助改善",
    title: "关注不适如何影响你的身体活动。",
    copy: "每次理疗都会根据你的感受和活动情况调整。Luma Care 可为以下情况提供支持：",
    disclaimer: "Luma Care 不进行医疗诊断。每个人的体验与效果可能不同。",
    items: [
      "头痛与偏头痛",
      "颈部与肩部疼痛",
      "上背部与下背部疼痛",
      "关节不适",
      "肌肉紧绷",
      "姿势相关疼痛",
      "产后恢复",
      "运动与锻炼后恢复",
      "活动能力下降",
      "年龄相关的身体僵硬",
    ],
  },
  service: {
    eyebrow: "主要服务",
    title: "康复理疗",
    chineseTitle: "筋膜物理康复",
    priceLabel: "$129",
    duration: "45 分钟",
    location: "上门服务",
    copy: "个性化专业手法护理，旨在帮助缓解不适、恢复活动能力，并改善身体动作。",
    book: "预约筋膜物理康复",
    noteLabel: "完成预约确认",
    note: "在线预约后，请将完整服务地址（包括房号及门禁密码，如适用）短信发送至 949-682-5516。收到地址后，预约才正式确认。",
  },
  offer: {
    eyebrow: "新客户礼遇",
    title: "首次理疗立减 $29。",
    copy: "新客户以及由亲友推荐的客户，可享受一次优惠预约。",
    book: "预约新客户理疗",
    finePrint: "每位符合条件的客户仅限一次优惠预约。",
  },
  process: {
    eyebrow: "预约流程",
    title: "从在线预约到上门服务。",
    steps: [
      ["在线预约", "选择服务项目和预约时间。"],
      ["短信发送地址", "发送完整服务地址和门禁信息。"],
      ["收到确认", "Luma Care 收到地址后确认预约。"],
      ["在家接受理疗", "April 根据你的身体状况和活动需求提供个性化服务。"],
    ],
  },
  about: {
    eyebrow: "关于 April",
    title: "以经验为基础，以真诚为原则。",
    copy: [
      "April 提供个性化手法康复理疗，重点关注身体活动、肌肉紧张、活动能力和实用恢复。",
      "她会根据每个人的情况调整理疗，并坚持提供诚实建议，不推荐没有必要或过于频繁的治疗。",
    ],
    imageAlt: "April 个人照片占位图",
    placeholder: "April 的个人照片即将更新",
    credentialsLabel: "经核实的专业资质将在此处更新。",
  },
  testimonials: {
    eyebrow: "客户体验",
    title: "让改善回到真实生活中。",
    disclaimer: "客户反馈经编辑节选。个人体验可能不同。",
    items: [
      {
        name: "Anthony，33 岁",
        location: "Fullerton, CA",
        quote: "仅仅接受 April 一次理疗后，我就感受到明显变化。将近两年过去了，我的偏头痛再也没有像以前那样反复出现。",
      },
      {
        name: "M Chen，28 岁",
        location: "Irvine, CA",
        quote: "尝试过针灸、按摩和其他治疗却没有明显改善后，与 April 合作让我感到下背部疼痛有了显著好转。",
      },
      {
        name: "Scott C.，33 岁",
        location: "Laguna Beach, CA",
        quote: "我的肩膀受限到手臂无法抬到 90 度。一次理疗后，活动就顺畅了许多。",
      },
      {
        name: "Camilla C.，30 岁",
        location: "Irvine, CA",
        quote: "三次理疗后，我的背痛显著改善。April 还帮助我调整锻炼方式，并拒绝推荐超出身体需要的理疗频率。",
      },
    ],
  },
  expectations: {
    eyebrow: "理疗体验",
    title: "这是专注的康复理疗，不是放松按摩。",
    copy: "处理紧绷或敏感部位时，手法可能会有较强的感觉。April 会根据你的反馈、舒适度和身体反应调整理疗过程，并鼓励你随时沟通。",
  },
  area: {
    eyebrow: "服务范围",
    title: "为橙县部分社区提供上门服务。",
    copy: "服务可用性取决于所在地点。请短信联系 949-682-5516，确认你的地址是否在当前服务范围内。",
    testimonialNote: "本页客户反馈来自 Fullerton、Irvine 和 Laguna Beach；这些地点并不代表完整服务范围。",
  },
  faq: {
    eyebrow: "常见问题",
    title: "预约前，你可能想了解。",
    items: [
      ["这是按摩服务吗？", "不是。这是旨在支持身体活动、灵活性与实用恢复的专注手法康复理疗，并非放松按摩。"],
      ["理疗会痛吗？", "处理紧绷或敏感部位时可能会有较强感觉。April 会根据你的反馈、舒适度和身体反应调整力度与方式。"],
      ["每次理疗多长时间？", "标准筋膜物理康复理疗为 45 分钟。"],
      ["在哪里接受理疗？", "在当前服务范围允许的情况下，Luma Care 会前往你提供的服务地址。"],
      ["如何确认预约？", "预约后，请将完整服务地址、房号及门禁密码（如适用）短信发送至 949-682-5516。收到地址后，预约才正式确认。"],
      ["理疗时应该穿什么？", "建议穿着舒适、便于活动，并能让需要关注的部位容易接触的衣服。"],
      ["你们服务哪些地区？", "Luma Care 为橙县部分社区提供服务。请将地址短信发送至 949-682-5516，确认当前是否可预约。"],
      ["适合产后恢复吗？", "产后恢复是 Luma Care 可以提供支持的方向之一。请在理疗前说明相关情况，并在适当时咨询你的医疗服务提供者。"],
      ["可以使用保险吗？", "保险参与和付款政策仍待确认。请短信联系 949-682-5516 获取最新信息。"], // REQUIRES BUSINESS CONFIRMATION
      ["取消预约的政策是什么？", "取消及改期政策仍待确认。如需了解当前条款，请在预约前联系 Luma Care。"], // REQUIRES BUSINESS CONFIRMATION
    ],
  },
  finalCta: {
    eyebrow: "按你的节奏接受护理",
    title: "足不出户，轻松改善身体状态。",
    copy: "选择合适的预约时间，然后发送服务地址以完成确认。",
  },
  footer: {
    tagline: "为橙县部分社区提供个性化上门康复理疗。",
    bookingNote: "预约将通过 Square 安全完成。",
    copyright: "Luma Care。保留所有权利。",
  },
};

export const siteContent = { en, zh };
export type SiteCopy = (typeof siteContent)[Language];
